import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Pill, Truck, Clock, HeartPulse, ShoppingBag, Store, Star, ArrowRight, Phone, MapPin, AlertTriangle, User, Loader2, X, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials, services, branches, findNearest24HourBranch, findNearestBranch } from '../data/mock';

const iconMap = {
  Pill,
  Truck,
  Clock,
  HeartPulse,
  ShoppingBag,
  Store
};

// Location Permission Modal Component
const LocationPermissionModal = ({ isOpen, onClose, onRetry, errorType, is24Hour }) => {
  if (!isOpen) return null;

  // Detect device type - must be computed inside component for each render
  const detectDeviceType = () => {
    const ua = navigator.userAgent || '';
    
    // iOS detection - check multiple patterns for reliability
    if (/iPhone|iPad|iPod/i.test(ua) || 
        (navigator.platform && /iPhone|iPad|iPod/.test(navigator.platform)) ||
        (navigator.maxTouchPoints > 1 && /Mac/.test(ua))) {
      return 'ios';
    }
    
    // Android detection
    if (/Android/i.test(ua)) {
      return 'android';
    }
    
    return 'desktop';
  };

  const deviceType = detectDeviceType();
  
  // Check if running as PWA (standalone mode)
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                window.navigator.standalone === true;

  const getInstructions = () => {
    const instructions = {
      ios: {
        title: isPWA ? 'Enable Location for this App' : 'Enable Location on iPhone/iPad',
        steps: isPWA ? [
          'Open the Settings app on your device',
          'Scroll down and tap "Privacy & Security"',
          'Tap "Location Services" and make sure it is ON',
          'Scroll down to find "Safari Websites" or this app',
          'Tap it and select "While Using" or "Ask Next Time"',
          'Return to this app and tap the button again'
        ] : [
          'Open the Settings app on your device',
          'Scroll down and tap "Safari"',
          'Scroll to "Settings for Websites" section',
          'Tap "Location"',
          'Select "Ask" (not "Deny")',
          'Return to Safari and tap the button again'
        ]
      },
      android: {
        title: 'Enable Location on Android',
        steps: [
          'Tap the lock icon in your browser address bar',
          'Tap "Site settings" or "Permissions"',
          'Find "Location" and tap "Allow"',
          'Alternatively: Go to Settings > Apps > Browser > Permissions > Location',
          'Return to this page and tap the button again'
        ]
      },
      desktop: {
        title: 'Enable Location in Browser',
        steps: [
          'Click the lock icon or info icon next to the website address',
          'Find "Location" in the permissions list',
          'Change it from "Block" to "Allow"',
          'Refresh this page and try again'
        ]
      }
    };

    return instructions[deviceType];
  };

  const { title, steps } = getInstructions();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-orange-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <MapPin className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Location Access Needed</h3>
                <p className="text-orange-100 text-sm">To find your nearest branch</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {errorType === 'denied' ? (
            <>
              <div className="flex items-center gap-2 text-red-600 mb-4">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">Location access was blocked</span>
              </div>
              <p className="text-gray-600 mb-4">
                To find the {is24Hour ? 'nearest 24-hour pharmacy' : 'closest branch'}, we need access to your location. 
                Please enable location permissions:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  {title}
                </h4>
                <ol className="space-y-2">
                  {steps.map((step, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="bg-orange-600 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </>
          ) : errorType === 'unavailable' ? (
            <>
              <div className="flex items-center gap-2 text-amber-600 mb-4">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">Location unavailable</span>
              </div>
              <p className="text-gray-600 mb-4">
                We couldn't determine your location. This might be because:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 mb-4 list-disc list-inside">
                <li>GPS is turned off on your device</li>
                <li>You're in an area with poor signal</li>
                <li>Your device doesn't support location services</li>
              </ul>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-amber-600 mb-4">
                <Clock className="w-5 h-5" />
                <span className="font-medium">Request timed out</span>
              </div>
              <p className="text-gray-600 mb-4">
                The location request took too long. Please make sure you have a good internet connection and try again.
              </p>
            </>
          )}

          {/* Alternative Option */}
          <div className="border-t pt-4 mt-4">
            <p className="text-sm text-gray-500 mb-3">
              Or view all our branches and choose one manually:
            </p>
            <div className="flex gap-3">
              <Button onClick={onRetry} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white">
                Try Again
              </Button>
              <Link to="/branches" className="flex-1">
                <Button variant="outline" className="w-full border-orange-600 text-orange-600 hover:bg-orange-50">
                  View All Branches
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Home = () => {
  const [products, setProducts] = useState([]);
  const [findingLocation, setFindingLocation] = useState(false);
  const [findingAnyLocation, setFindingAnyLocation] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [locationErrorType, setLocationErrorType] = useState('denied');
  const [is24HourSearch, setIs24HourSearch] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data.slice(0, 4)); // Get first 4 products
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const openDefaultBranch = (is24Hour) => {
    const branch = is24Hour ? branches.find(b => b.is24Hours) : branches[0];
    if (branch) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${branch.coordinates.lat},${branch.coordinates.lng}&travelmode=driving`, '_blank');
    }
  };

  const handleLocationError = (error, is24Hour) => {
    let errorType = 'denied';
    
    switch(error.code) {
      case error.PERMISSION_DENIED:
        errorType = 'denied';
        break;
      case error.POSITION_UNAVAILABLE:
        errorType = 'unavailable';
        break;
      case error.TIMEOUT:
        errorType = 'timeout';
        break;
      default:
        errorType = 'denied';
    }
    
    setLocationErrorType(errorType);
    setIs24HourSearch(is24Hour);
    setShowPermissionModal(true);
  };

  const findNearest24Hour = () => {
    setFindingLocation(true);
    
    if (!navigator.geolocation) {
      setLocationErrorType('unavailable');
      setIs24HourSearch(true);
      setShowPermissionModal(true);
      setFindingLocation(false);
      return;
    }

    // Detect iOS for special handling
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    
    let locationReceived = false;
    
    // Manual timeout for iOS PWA which can hang without calling error callback
    const manualTimeout = setTimeout(() => {
      if (!locationReceived) {
        console.log('[Branch Finder] Manual timeout triggered');
        setLocationErrorType('timeout');
        setIs24HourSearch(true);
        setShowPermissionModal(true);
        setFindingLocation(false);
      }
    }, isIOS ? 20000 : 15000);

    console.log('[Branch Finder] Requesting geolocation...');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        locationReceived = true;
        clearTimeout(manualTimeout);
        
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;
        
        console.log('[Branch Finder] Got location:', userLat, userLng);
        
        const nearest = findNearest24HourBranch(userLat, userLng);
        
        console.log('[Branch Finder] Nearest 24hr branch:', nearest);
        
        if (nearest) {
          const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${nearest.coordinates.lat},${nearest.coordinates.lng}&travelmode=driving`;
          
          console.log('[Branch Finder] Opening directions:', directionsUrl);
          
          // Use location.href for all devices (works on iOS Safari)
          window.location.href = directionsUrl;
        } else {
          alert('No 24-hour branches found.');
          setFindingLocation(false);
        }
      },
      (error) => {
        locationReceived = true;
        clearTimeout(manualTimeout);
        console.error('[Branch Finder] Geolocation error:', error.code, error.message);
        handleLocationError(error, true);
        setFindingLocation(false);
      },
      { 
        enableHighAccuracy: false,
        timeout: isIOS ? 15000 : 10000,
        maximumAge: 60000
      }
    );
  };

  const findNearestAny = () => {
    setFindingAnyLocation(true);
    
    if (!navigator.geolocation) {
      setLocationErrorType('unavailable');
      setIs24HourSearch(false);
      setShowPermissionModal(true);
      setFindingAnyLocation(false);
      return;
    }

    // Detect iOS for special handling
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    
    let locationReceived = false;
    
    // Manual timeout for iOS PWA which can hang without calling error callback
    const manualTimeout = setTimeout(() => {
      if (!locationReceived) {
        console.log('[Branch Finder] Manual timeout triggered');
        setLocationErrorType('timeout');
        setIs24HourSearch(false);
        setShowPermissionModal(true);
        setFindingAnyLocation(false);
      }
    }, isIOS ? 20000 : 15000);

    console.log('[Branch Finder] Requesting geolocation...');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        locationReceived = true;
        clearTimeout(manualTimeout);
        
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;
        
        console.log('[Branch Finder] Got location:', userLat, userLng);
        
        const nearest = findNearestBranch(userLat, userLng);
        
        console.log('[Branch Finder] Nearest branch:', nearest);
        
        if (nearest) {
          const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${nearest.coordinates.lat},${nearest.coordinates.lng}&travelmode=driving`;
          
          console.log('[Branch Finder] Opening directions:', directionsUrl);
          
          // Use location.href for all devices (works on iOS Safari)
          window.location.href = directionsUrl;
        } else {
          alert('No branches found.');
          setFindingAnyLocation(false);
        }
      },
      (error) => {
        locationReceived = true;
        clearTimeout(manualTimeout);
        console.error('[Branch Finder] Geolocation error:', error.code, error.message);
        handleLocationError(error, false);
        setFindingAnyLocation(false);
      },
      { 
        enableHighAccuracy: false,
        timeout: isIOS ? 15000 : 10000,
        maximumAge: 60000
      }
    );
  };

  const handleRetryLocation = () => {
    setShowPermissionModal(false);
    if (is24HourSearch) {
      findNearest24Hour();
    } else {
      findNearestAny();
    }
  };

  return (
    <div className="min-h-screen">
      {/* Location Permission Modal */}
      <LocationPermissionModal
        isOpen={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
        onRetry={handleRetryLocation}
        errorType={locationErrorType}
        is24Hour={is24HourSearch}
      />

      {/* Emergency Branch Finder - Sticky Banner */}
      <div className="bg-red-600 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 animate-pulse" />
              <span className="font-medium">Need medicines urgently?</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={findNearest24Hour}
                disabled={findingLocation}
                className="flex items-center gap-2 bg-white text-red-600 px-4 py-2 rounded-full font-semibold hover:bg-red-50 transition-colors disabled:opacity-70 text-sm"
              >
                {findingLocation ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Finding...
                  </>
                ) : (
                  <>
                    <Clock className="w-4 h-4" />
                    Find Nearest 24-Hour Pharmacy
                  </>
                )}
              </button>
              <button
                onClick={findNearestAny}
                disabled={findingAnyLocation}
                className="flex items-center gap-2 bg-red-700 text-white px-4 py-2 rounded-full font-semibold hover:bg-red-800 transition-colors disabled:opacity-70 border border-red-400 text-sm"
              >
                {findingAnyLocation ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Finding...
                  </>
                ) : (
                  <>
                    <MapPin className="w-4 h-4" />
                    Find Nearest Pharmacy
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-6">
                ⭐ Trusted Community Pharmacy | 5.0 Rating
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Your Health,
                <span className="text-orange-600"> Our Priority</span>
              </h1>
              <p className="!text-lg mb-8 text-gray-600">Quality medicines and professional healthcare services available 24/7 across Accra. City Central Pharmacy - Pharmacy for everyone.


              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/order-medicines">
                  <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white px-8">
                    Order Medicines
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/insurance-prescription">
                  <Button size="lg" variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50 px-8">
                    Upload Insurance Prescription
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex gap-8">
                <div>
                  <div className="text-3xl font-bold text-orange-600">5</div>
                  <div className="text-sm text-gray-600">Branches</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-600">24/7</div>
                  <div className="text-sm text-gray-600">Available</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-600">5.0⭐</div>
                  <div className="text-sm text-gray-600">Google Rating</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_medlocator-26/artifacts/k93bku7z_DSC09557.jpeg"
                  alt="City Central Pharmacy - Quality Medicines and Healthcare Products"
                  className="w-full h-auto" />

              </div>
              <div className="absolute -bottom-6 -left-6 bg-yellow-400 text-gray-900 px-6 py-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="font-bold">Open Now - 24 Hours</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive pharmaceutical services designed to meet all your healthcare needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-none shadow-md hover:shadow-xl transition-all hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Featured Products - Smooth Scrolling Carousel */}
      <section className="py-16 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Featured Products</h2>
              <p className="text-gray-600">Quality medicines and healthcare products</p>
            </div>
            <Link to="/products">
              <Button variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50 hidden sm:flex">
                View All
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          {/* Scrolling Carousel */}
          <div className="relative">
            <div 
              className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory scroll-smooth"
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {products.length > 0 ? products.map((product) => (
                <Card key={product.id} className="flex-shrink-0 w-[280px] sm:w-[300px] overflow-hidden hover:shadow-xl transition-all snap-start">
                  <div className="h-48 bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center overflow-hidden">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <img 
                        src={`https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=300&h=200&fit=crop`} 
                        alt={product.name} 
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <CardContent className="p-4">
                    <div className="text-xs text-orange-600 font-semibold mb-1">{product.category || 'General'}</div>
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-orange-600">GH₵ {(product.price || 0).toFixed(2)}</span>
                      <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white">
                        Add
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )) : (
                // Fallback featured products with images
                [
                  { id: 1, name: 'Paracetamol 500mg', category: 'Pain Relief', price: 15.00, image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=300&h=200&fit=crop' },
                  { id: 2, name: 'Vitamin C 1000mg', category: 'Supplements', price: 45.00, image: 'https://images.unsplash.com/photo-1556228578-8c89e6adf883?w=300&h=200&fit=crop' },
                  { id: 3, name: 'First Aid Kit', category: 'First Aid', price: 85.00, image: 'https://images.unsplash.com/photo-1603398938378-e54eab446dde?w=300&h=200&fit=crop' },
                  { id: 4, name: 'Blood Pressure Monitor', category: 'Medical Devices', price: 250.00, image: 'https://images.unsplash.com/photo-1559757175-5700dde675bc?w=300&h=200&fit=crop' },
                  { id: 5, name: 'Baby Care Set', category: 'Baby Care', price: 120.00, image: 'https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=300&h=200&fit=crop' },
                ].map((product) => (
                  <Card key={product.id} className="flex-shrink-0 w-[280px] sm:w-[300px] overflow-hidden hover:shadow-xl transition-all snap-start">
                    <div className="h-48 bg-gradient-to-br from-orange-50 to-orange-100 overflow-hidden">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                    </div>
                    <CardContent className="p-4">
                      <div className="text-xs text-orange-600 font-semibold mb-1">{product.category}</div>
                      <h3 className="font-semibold text-gray-900 mb-2">{product.name}</h3>
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold text-orange-600">GH₵ {product.price.toFixed(2)}</span>
                        <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white">
                          Add
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
            
            {/* Scroll hint for mobile */}
            <div className="flex justify-center mt-4 sm:hidden">
              <span className="text-xs text-gray-400">← Swipe to see more →</span>
            </div>
          </div>
          
          {/* Mobile View All button */}
          <div className="mt-6 text-center sm:hidden">
            <Link to="/products">
              <Button variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="flex">
                {[...Array(5)].map((_, i) =>
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                )}
              </div>
              <span className="text-gray-600 font-semibold">5.0 out of 5</span>
            </div>
            <p className="text-gray-600">Based on Google reviews</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) =>
            <Card key={testimonial.id} className="border-none shadow-md">
                <CardContent className="p-6">
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) =>
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  )}
                  </div>
                  <p className="text-gray-700 mb-4">"{testimonial.comment}"</p>
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-semibold text-gray-900">{testimonial.name}</span>
                    <span className="text-gray-500">{testimonial.date}</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Medicines Urgently?</h2>
          <p className="text-xl mb-8 text-orange-100">We're here for you 24/7. Call us or order online now!</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="tel:+233539543737">
              <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100">
                <Phone className="mr-2 w-5 h-5" />
                Call Now
              </Button>
            </a>
            <Link to="/order-medicines">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-orange-700">
                Order Online
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};
